import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Film } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const HomePage = () => {
  const [hoveredSide, setHoveredSide] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#030303]">
      <SEO brand="unified" path="/" />
      <Navigation brand="split" />

      {/* Split Hero Section */}
      <div className="relative h-screen w-full overflow-hidden mt-20">
        <div className="absolute inset-0 flex">
          {/* Brandify Side */}
          <motion.div
            className="relative overflow-hidden cursor-pointer"
            style={{
              width: hoveredSide === "brandify" ? "75%" : hoveredSide === "studio" ? "25%" : "50%",
              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
            onMouseEnter={() => setHoveredSide("brandify")}
            onMouseLeave={() => setHoveredSide(null)}
            onClick={() => navigate("/brandify/services")}
            data-testid="brandify-split-section"
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1754348523191-6705920cdb4a?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                filter: hoveredSide === "studio" ? "brightness(0.3)" : "brightness(0.5)"
              }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1cb5ae]/40 to-[#1aada5]/40" />
            
            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Sparkles className="w-16 h-16 text-[#1cb5ae] mb-6 mx-auto" />
                <h1 className="heading-font text-5xl md:text-7xl text-white mb-4">
                  BRANDIFY OFFICIAL
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 brandify-body max-w-xl mx-auto">
                  AI-Driven Marketing. Real-World Growth.
                </p>
                {hoveredSide === "brandify" && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-effect px-8 py-4 rounded-full text-white font-medium flex items-center gap-2 mx-auto hover:bg-[#1cb5ae]/20 transition-all"
                    data-testid="explore-brandify-btn"
                  >
                    Explore Brandify
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                )}
              </motion.div>
            </div>
          </motion.div>

          {/* Vertical Divider */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-32 bg-white/20 z-10" />

          {/* Studio Side */}
          <motion.div
            className="relative overflow-hidden cursor-pointer"
            style={{
              width: hoveredSide === "studio" ? "75%" : hoveredSide === "brandify" ? "25%" : "50%",
              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
            onMouseEnter={() => setHoveredSide("studio")}
            onMouseLeave={() => setHoveredSide(null)}
            onClick={() => navigate("/studio/services")}
            data-testid="studio-split-section"
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1576987212553-c43b8e44bb1f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                filter: hoveredSide === "brandify" ? "brightness(0.3)" : "brightness(0.5)"
              }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-bl from-[#F97316]/40 to-[#DC2626]/40" />
            
            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-center p-8 text-center film-grain">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Film className="w-16 h-16 text-[#F97316] mb-6 mx-auto" />
                <h1 className="heading-font text-5xl md:text-7xl text-white mb-4">
                  WAKE N' BAKE STUDIO
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 studio-body max-w-xl mx-auto">
                  We Don't Just Shoot. We Tell Stories.
                </p>
                {hoveredSide === "studio" && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass-effect px-8 py-4 rounded-full text-white font-medium flex items-center gap-2 mx-auto hover:bg-[#F97316]/20 transition-all"
                    data-testid="explore-studio-btn"
                  >
                    Explore Studio
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Mobile View Info */}
        <div className="absolute bottom-8 left-0 right-0 md:hidden px-6">
          <div className="glass-effect rounded-2xl p-6 text-center">
            <p className="text-sm text-gray-300 mb-4">Tap to explore each brand</p>
            <div className="flex gap-4">
              <button
                onClick={() => navigate("/brandify/services")}
                className="flex-1 bg-[#1cb5ae] text-white py-3 rounded-lg font-medium hover:bg-[#1aada5] transition-colors"
                data-testid="mobile-brandify-btn"
              >
                Brandify
              </button>
              <button
                onClick={() => navigate("/studio/services")}
                className="flex-1 bg-[#F97316] text-white py-3 rounded-lg font-medium"
                data-testid="mobile-studio-btn"
              >
                Studio
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* What We Do Section */}
      <section className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-6xl text-white mb-6">
              TWO BRANDS. ONE VISION.
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We combine AI-powered marketing strategies with cinematic storytelling to create brands that grow and stories that resonate.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Brandify Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all cursor-pointer group"
              onClick={() => navigate("/brandify/services")}
              data-testid="home-brandify-card"
            >
              <div className="w-16 h-16 rounded-full bg-[#1cb5ae]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-8 h-8 text-[#1cb5ae]" />
              </div>
              <h3 className="heading-font text-3xl text-[#1cb5ae] mb-4">BRANDIFY OFFICIAL</h3>
              <p className="text-gray-300 mb-6 brandify-body">
                AI-powered marketing agency specializing in rapid growth, performance marketing, and custom tech solutions. We build strategies that scale.
              </p>
              <div className="flex items-center text-[#1cb5ae] font-medium group-hover:gap-3 gap-2 transition-all">
                Learn More <ArrowRight className="w-5 h-5" />
              </div>
            </motion.div>

            {/* Studio Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all cursor-pointer group"
              onClick={() => navigate("/studio/services")}
              data-testid="home-studio-card"
            >
              <div className="w-16 h-16 rounded-full bg-[#F97316]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Film className="w-8 h-8 text-[#F97316]" />
              </div>
              <h3 className="heading-font text-3xl text-[#F97316] mb-4">WAKE N' BAKE STUDIO</h3>
              <p className="text-gray-300 mb-6 studio-body">
                Creative production house crafting cinematic brand films, digital commercials, and visual stories that move audiences and drive action.
              </p>
              <div className="flex items-center text-[#F97316] font-medium group-hover:gap-3 gap-2 transition-all">
                View Portfolio <ArrowRight className="w-5 h-5" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer brand="split" />
    </div>
  );
};

export default HomePage;
